import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import Card from '@/views/dashboard/_components/card/card.vue'
import RankingTable from './_components/ranking-table.vue'
import CategoryProportion from './_components/category-proportion.vue'
import type { RankingTableColumn, CategoryProportionItem } from '../types'
import TimeRangeSelect from '@/views/dashboard/_components/time-range-select/time-range-select.vue'
import { TimeRange } from '@/views/dashboard/_components/time-range-select/time-range.enum'
import { OnlineControlZoneService } from './online-control-zone.service'
import { getTimeRangeByEnum } from '@/utils/time'
import { bytesToSize } from '@/utils/tool'
import { getRiskPortDisplayText } from '@/utils/port-risk-display'


export default /*#__PURE__*/_defineComponent({
  __name: 'online-control-zone',
  setup(__props) {

const { t,te, locale } = useI18n()
const router = useRouter()
const injector = useReflectiveInjector([OnlineControlZoneService])
const service = injector.get(OnlineControlZoneService)

const timeRange = ref<TimeRange>(TimeRange.DAYS_30)

// 域名拦截排行数据
const domainRankingData = ref<Array<{ rank: number; domain: string; count: number }>>([])
const domainColumns = computed<RankingTableColumn[]>(() => {
  void locale.value
  return [
    { prop: 'domain', label: t('overviewZone.colDomain') },
    { prop: 'count', label: t('overviewZone.colDomainHitCount'), width: 140, columnOpt: { align: 'right' } }
  ]
})

// 上网分类占比数据
const categoryData = ref<CategoryProportionItem[]>([])

// IP拦截排行数据
const ipRankingData = ref<Array<{ rank: number; ip: string; count: number }>>([])
const ipColumns = computed<RankingTableColumn[]>(() => {
  void locale.value
  return [
    { prop: 'ip', label: t('overviewZone.colIp') },
    { prop: 'count', label: t('overviewZone.colIpInterceptCount'), width: 140, columnOpt: { align: 'right' } }
  ]
})

// 风险端口排行数据
const portRankingData = ref<Array<{ rank: number; port: string; description: string; hostCount: number,system_type: number }>>([])
const portColumns = computed<RankingTableColumn[]>(() => {
  void locale.value
  return [
    { prop: 'port', label: t('overviewZone.colPort'), width: 60 },
    { prop: 'description', label: t('overviewZone.colDescription'), columnOpt: { minWidth: 200,showOverflow: true } },
    { prop: 'hostCount', label: t('overviewZone.colOpenHosts'), width: 140, columnOpt: { align: 'right' } }
  ]
})

// 终端流量排行数据
const trafficRankingData = ref<Array<{ rank: number; user: string; group: string; traffic: string }>>([])
const trafficColumns = computed<RankingTableColumn[]>(() => {
  void locale.value
  return [
    { prop: 'user', label: t('overviewZone.colUser') },
    { prop: 'group', label: t('overviewZone.colGroup') },
    { prop: 'traffic', label: t('overviewZone.colTrafficUsage'), columnOpt: { align: 'right' } }
  ]
})

// 加载域名拦截排行
async function loadDomainRanking() {
  try {
    const [start_time, end_time] = getTimeRangeByEnum(timeRange.value)
    const res = await service.getDomainRanking({
      top_count: 5,
      start_time: start_time?.toString(),
      end_time: end_time?.toString()
    })
    domainRankingData.value = (res.top_domain_list || []).map((item, index) => ({
      rank: index + 1,
      domain: item.domain || t('overviewZone.unknown'),
      count: item.block_count || 0
    }))
  } catch (error) {
    console.error('加载域名拦截排行失败:', error)
    domainRankingData.value = []
  }
}

// 加载IP拦截排行
async function loadIpRanking() {
  try {
    const [start_time, end_time] = getTimeRangeByEnum(timeRange.value)
    const res = await service.getIpRanking({
      top_count: 5,
      start_time: start_time?.toString(),
      end_time: end_time?.toString()
    })
    ipRankingData.value = (res.top_ip_list || []).map((item, index) => ({
      rank: index + 1,
      ip: item.ip_address || t('overviewZone.unknown'),
      count: item.block_count || 0
    }))
  } catch (error) {
    console.error('加载IP拦截排行失败:', error)
    ipRankingData.value = []
  }
}

// 加载风险端口排行
async function loadPortRanking() {
  try {
    const res = await service.getPortRanking({
      top_count: 5
    })
    portRankingData.value = (res.port_list || []).map((item, index) => ({
      rank: index + 1,
      port: String(item.port_num || t('overviewZone.unknown')),
      description: getRiskPortDisplayText(t, te, item.port_num, item.system_type, item.risk_detail) || t('overviewZone.unknownRisk'),
      hostCount: item.device_count || 0,
      system_type:item.system_type
    })).splice(0, 5)
  } catch (error) {
    console.error('加载风险端口排行失败:', error)
    portRankingData.value = []
  }
}

// 加载终端流量排行
async function loadTrafficRanking() {
  try {
    const [start_time, end_time] = getTimeRangeByEnum(timeRange.value)
    const res = await service.getTrafficRanking({
      top_count: 5,
      start_time: start_time?.toString(),
      end_time: end_time?.toString()
    })
    trafficRankingData.value = (res.top_terminal_list || []).map((item, index) => ({
      rank: index + 1,
      user: item.computer_name || t('overviewZone.unknown'),
      group: item.department || t('overviewZone.ungrouped'),
      traffic: bytesToSize(item.total_flow) || '0 GB'
    })).splice(0, 5)
  } catch (error) {
    console.error('加载终端流量排行失败:', error)
    trafficRankingData.value = []
  }
}

// 加载上网分类占比
async function loadCategoryProportion() {
  try {
    const [start_time, end_time] = getTimeRangeByEnum(timeRange.value)
    const res = await service.getCategoryProportion({
      top_count: 5,
      start_time: start_time?.toString(),
      end_time: end_time?.toString()
    })
    categoryData.value = (res.top_category_list || []).map((item, index) => ({
      rank: index + 1,
      category: item.category_name || t('overviewZone.unknown'),
      proportion: item.ratio || 0
    })).splice(0, 5)
  } catch (error) {
    console.error('加载上网分类占比失败:', error)
    categoryData.value = []
  }
}

// 加载所有数据
async function loadAllData() {
  await Promise.all([
    loadDomainRanking(),
    loadIpRanking(),
    loadPortRanking(),
    loadTrafficRanking(),
    loadCategoryProportion()
  ])
}

// 跳转功能
function handleViewMoreDomain() {
  router.push('/dashboard/access-restriction')
}

function handleViewMoreIP() {
  router.push('/dashboard/ip-address-blocking')
}

function handleViewMorePort() {
  router.push('/dashboard/port-manage?risk_type=1')
}

function handleViewMoreTraffic() {
  router.push('/dashboard/terminal-traffic-state-list')
}

// 监听时间范围变化
watch(timeRange, () => {
  loadAllData()
})

onMounted(() => {
  loadAllData()
})

return (_ctx: any,_cache: any) => {
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_col = _resolveComponent("el-col")!
  const _component_el_row = _resolveComponent("el-row")!

  return (_openBlock(), _createBlock(Card, {
    size: "first",
    "background-color": "var(--cm-color-card-bg)",
    border: "",
    title: _ctx.$t('overviewZone.onlineControlTitle'),
    description: _ctx.$t('overviewZone.onlineControlDesc')
  }, {
    headerRight: _withCtx(() => [
      _createVNode(TimeRangeSelect, {
        modelValue: timeRange.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((timeRange).value = $event))
      }, null, 8, ["modelValue"])
    ]),
    default: _withCtx(() => [
      _createVNode(_component_el_row, { gutter: 15 }, {
        default: _withCtx(() => [
          _createVNode(_component_el_col, {
            span: 12,
            class: "cm-ui-mb15"
          }, {
            default: _withCtx(() => [
              _createVNode(Card, {
                size: "second",
                title: _ctx.$t('overviewZone.domainInterceptRankTop5'),
                style: {"height":"100%"},
                "on-view-more": handleViewMoreDomain
              }, {
                titleAfter: _withCtx(() => [
                  _createVNode(_component_el_tag, {
                    size: "small",
                    type: "primary"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('common.rankTagTop5')), 1)
                    ]),
                    _: 1
                  })
                ]),
                default: _withCtx(() => [
                  _createVNode(RankingTable, {
                    data: domainRankingData.value,
                    columns: domainColumns.value
                  }, null, 8, ["data", "columns"])
                ]),
                _: 1
              }, 8, ["title"])
            ]),
            _: 1
          }),
          _createVNode(_component_el_col, {
            span: 12,
            class: "cm-ui-mb15"
          }, {
            default: _withCtx(() => [
              _createVNode(Card, {
                size: "second",
                title: _ctx.$t('overviewZone.categoryProportionTop5'),
                style: {"height":"100%"}
              }, {
                titleAfter: _withCtx(() => [
                  _createVNode(_component_el_tag, {
                    size: "small",
                    type: "primary"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('common.rankTagTop5')), 1)
                    ]),
                    _: 1
                  })
                ]),
                default: _withCtx(() => [
                  _createVNode(CategoryProportion, { data: categoryData.value }, null, 8, ["data"])
                ]),
                _: 1
              }, 8, ["title"])
            ]),
            _: 1
          }),
          _createVNode(_component_el_col, { span: 8 }, {
            default: _withCtx(() => [
              _createVNode(Card, {
                size: "second",
                title: _ctx.$t('overviewZone.ipInterceptRankTop5'),
                style: {"height":"100%"},
                "on-view-more": handleViewMoreIP
              }, {
                titleAfter: _withCtx(() => [
                  _createVNode(_component_el_tag, {
                    size: "small",
                    type: "primary"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('common.rankTagTop5')), 1)
                    ]),
                    _: 1
                  })
                ]),
                default: _withCtx(() => [
                  _createVNode(RankingTable, {
                    data: ipRankingData.value,
                    columns: ipColumns.value
                  }, null, 8, ["data", "columns"])
                ]),
                _: 1
              }, 8, ["title"])
            ]),
            _: 1
          }),
          _createVNode(_component_el_col, { span: 8 }, {
            default: _withCtx(() => [
              _createVNode(Card, {
                size: "second",
                title: _ctx.$t('overviewZone.riskPortRankTop5'),
                style: {"height":"100%"},
                "on-view-more": handleViewMorePort
              }, {
                titleAfter: _withCtx(() => [
                  _createVNode(_component_el_tag, {
                    size: "small",
                    type: "primary"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('common.rankTagTop5')), 1)
                    ]),
                    _: 1
                  })
                ]),
                default: _withCtx(() => [
                  _createVNode(RankingTable, {
                    data: portRankingData.value,
                    columns: portColumns.value
                  }, null, 8, ["data", "columns"])
                ]),
                _: 1
              }, 8, ["title"])
            ]),
            _: 1
          }),
          _createVNode(_component_el_col, { span: 8 }, {
            default: _withCtx(() => [
              _createVNode(Card, {
                size: "second",
                title: _ctx.$t('overviewZone.terminalTrafficRankTop5'),
                style: {"height":"100%"},
                "on-view-more": handleViewMoreTraffic
              }, {
                titleAfter: _withCtx(() => [
                  _createVNode(_component_el_tag, {
                    size: "small",
                    type: "primary"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('common.rankTagTop5')), 1)
                    ]),
                    _: 1
                  })
                ]),
                default: _withCtx(() => [
                  _createVNode(RankingTable, {
                    data: trafficRankingData.value,
                    columns: trafficColumns.value
                  }, null, 8, ["data", "columns"])
                ]),
                _: 1
              }, 8, ["title"])
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["title", "description"]))
}
}

})